import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { useLanguage } from "../i18n/LanguageContext";
import { IMG, BUSINESS } from "../lib/constants";
import TrustStrip from "../components/TrustStrip";
import ReviewsSection from "../components/ReviewsSection";
import BookingCTA from "../components/BookingCTA";
import SEOHead from "../components/SEOHead";

const About = () => {
    const { t } = useLanguage();
    const a = t.about;

    return (
        <>
            <SEOHead page="about" />
            {/* Hero — split layout to showcase the portrait image (which has its own text overlay) */}
            <section
                data-testid="about-hero"
                className="bg-[#3D3D24] text-[#FFFFF3] overflow-hidden"
            >
                <div className="max-w-[1400px] mx-auto grid lg:grid-cols-2 gap-0">
                    <div className="order-2 lg:order-1 px-6 md:px-12 lg:px-16 py-16 md:py-24 lg:py-28 flex flex-col justify-center">
                        <div className="font-label text-[11px] text-[#D4A95A] mb-5">{a.heroOverline}</div>
                        <h1 className="font-serif text-5xl md:text-6xl lg:text-7xl leading-[0.98] mb-6">
                            {a.heroTitle}
                        </h1>
                        <div className="font-label text-[11px] tracking-[0.25em] opacity-80 mb-5">
                            PMUA · ADVANCED ESTHETICIAN · FOUNDER · EST. 2015
                        </div>
                        <p className="text-base md:text-lg leading-relaxed opacity-85 max-w-xl">
                            Founder of Beauty Ink LV. A decade of practice. A bilingual studio just minutes from LAS.
                        </p>
                    </div>
                    <div className="order-1 lg:order-2 relative">
                        <img
                            src={IMG.wendyMain}
                            alt="Wendy Chester - Permanent Makeup Artist Las Vegas, PMUA, Advanced Esthetician, Beauty Ink LV founder"
                            className="w-full h-full object-cover object-top min-h-[420px] lg:min-h-[560px]"
                        />
                    </div>
                </div>
            </section>

            <TrustStrip variant="dark" />

            {/* Story */}
            <section className="bg-[#FFFFF3] py-24 md:py-32">
                <div className="max-w-[900px] mx-auto px-6 space-y-8">
                    <p className="font-serif text-2xl md:text-3xl text-[#3D3D24] leading-snug">
                        {a.body1}
                    </p>
                    <p className="text-base md:text-lg text-[#3D3D24]/85 leading-relaxed">
                        {a.body2}
                    </p>
                    <p className="text-base md:text-lg text-[#3D3D24]/85 leading-relaxed italic border-l-2 border-[#D4A95A] pl-6">
                        {a.body3}
                    </p>
                </div>
            </section>

            {/* Studio gallery - Wendy photos only */}
            <section className="bg-[#F5F2E4] py-24 md:py-32">
                <div className="max-w-[1400px] mx-auto px-6">
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-3 md:gap-5">
                        {[IMG.wendyAbout, IMG.wendy2, IMG.wendy3, IMG.wendyOvertop, IMG.wendyMentor].map((src, i) => (
                            <div
                                key={i}
                                data-testid={`about-gallery-${i}`}
                                className={`overflow-hidden ${i === 0 ? "aspect-[4/5] md:row-span-2" : "aspect-square"} bg-[#F5F2E4]`}
                            >
                                <img src={src} alt={`Wendy Chester permanent makeup artist Las Vegas Beauty Ink LV ${i + 1}`} className="w-full h-full object-cover" />
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            <ReviewsSection />

            {/* Quick Links */}
            <section className="bg-[#3D3D24] text-[#FFFFF3] py-24 md:py-32">
                <div className="max-w-[1100px] mx-auto px-6 grid md:grid-cols-3 gap-8">
                    <Link
                        data-testid="about-link-bed-rentals"
                        to="/bed-rentals"
                        className="border border-[#FFFFF3]/20 p-8 hover:bg-[#D4A95A] hover:text-[#3D3D24] transition-colors group"
                    >
                        <div className="font-label text-[10px] text-[#D4A95A] group-hover:text-[#3D3D24]/70 mb-3">For Artists</div>
                        <h3 className="font-serif text-2xl mb-3">Bed Rentals</h3>
                        <div className="flex items-center gap-1.5 font-label text-xs">Learn more <ArrowRight size={14} /></div>
                    </Link>
                    <Link
                        data-testid="about-link-training"
                        to="/training"
                        className="border border-[#FFFFF3]/20 p-8 hover:bg-[#D4A95A] hover:text-[#3D3D24] transition-colors group"
                    >
                        <div className="font-label text-[10px] text-[#D4A95A] group-hover:text-[#3D3D24]/70 mb-3">Apprenticeship</div>
                        <h3 className="font-serif text-2xl mb-3">6-Month Training</h3>
                        <div className="flex items-center gap-1.5 font-label text-xs">Apply now <ArrowRight size={14} /></div>
                    </Link>
                    <a
                        data-testid="about-link-book"
                        href="/contact"
                        className="border border-[#FFFFF3]/20 p-8 bg-[#D4A95A] text-[#3D3D24] hover:bg-[#FFFFF3] transition-colors group"
                    >
                        <div className="font-label text-[10px] text-[#3D3D24]/70 mb-3">For Clients</div>
                        <h3 className="font-serif text-2xl mb-3">Book a Service</h3>
                        <div className="flex items-center gap-1.5 font-label text-xs">Reserve now <ArrowRight size={14} /></div>
                    </a>
                </div>
            </section>

            <BookingCTA variant="light" testid="booking-cta-about" />
        </>
    );
};

export default About;
